/**
 * LightLayer FALLBACK MODE — MapLibre line layers instead of the custom WebGL layer.
 *
 * Used when a shader fails to compile/link, an offscreen framebuffer is
 * incomplete, or the URL says ?light=fallback. Weaker by design (spec §D.8):
 *
 *   hero (one route)  SOURCE_IDS.fallbackRoute (lineMetrics) with three layers:
 *                     LAYER_IDS.fallbackGlow  blurred line, width × 4, line-blur 6, opacity 0.35 × gain
 *                     ld-sel-shade            contact shade, width + --ld-route-shade-px
 *                     LAYER_IDS.fallbackCore  core; line-gradient stepped at tail/head each frame
 *   everything else   ld-sel-rest source: static whole lines, per-path alpha via feature-state
 *   comet             a DOM marker (.ld-loc-gl-comet) at the head
 *
 * All layers sit just beneath `beforeId` (ANCHORS.routes), bottom → top in the order above
 * (rest shade, rest core, glow, shade, core).
 */
import * as maplibregl from "maplibre-gl";
import type { FeatureCollection } from "geojson";
import type { ExpressionSpecification, GeoJSONSource, LineLayerSpecification, Map as MLMap, Marker } from "maplibre-gl";
import { LAYER_IDS, SOURCE_IDS } from "@/contracts/map";
import type { LngLat } from "@/lib/geo";
import { cssRGBA, mixRGB, opaqueShade, type GLPalette, type MoodLight, type RGB } from "./color";
import { progressGradient } from "./gradient";
import type { PackedPath } from "./pack";
import type { LightStyle } from "./state";
import "./gl.css";

export const FALLBACK_IDS = {
  restSource: "ld-sel-rest",
  restShade: "ld-sel-rest-shade",
  restCore: "ld-sel-rest-core",
  shade: "ld-sel-shade",
} as const;

export interface FallbackHero {
  path: PackedPath;
  headM: number; //  drawn metres
  tailM: number;
  alpha: number;
  width: number;
  glow: number;
  trail: number; //  0..1 trail amount
  style: LightStyle;
}

export interface FallbackRest {
  path: PackedPath;
  alpha: number;
  width: number;
  style: LightStyle;
}

export interface FallbackFrame {
  hero: FallbackHero | null;
  rest: FallbackRest[];
  palette: GLPalette;
  light: MoodLight;
  comet: { lngLat: LngLat; alpha: number; scale: number } | null;
}

type PaintName = Parameters<MLMap["setPaintProperty"]>[1];
type PaintValue = Parameters<MLMap["setPaintProperty"]>[2];

const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };

const styleColor = (p: GLPalette, s: LightStyle): RGB => (s === "bloom" ? p.bloom : s === "preview" ? p.preview : p.core);

export class LightFallback {
  private map: MLMap;
  private beforeId: string;
  private paths: PackedPath[] = [];
  private heroId: string | null = null;
  private restKey = "";
  private lastPaint = new Map<string, string>();
  private restAlpha = new Map<number, number>();
  private marker: Marker | null = null;
  private markerEl: HTMLDivElement | null = null;
  private markerOn = false;
  private destroyed = false;

  constructor(map: MLMap, beforeId: string) {
    this.map = map;
    this.beforeId = beforeId;
  }

  /**
   * Add sources and layers that are missing (idempotent; call again after a style reload).
   * Returns how many were added (0 = all present), or -1 when the style is not ready.
   */
  attach(): number {
    const map = this.map;
    let added = 0;
    try {
      const before = this.beforeId && map.getLayer(this.beforeId) ? this.beforeId : undefined;
      if (!map.getSource(FALLBACK_IDS.restSource)) {
        map.addSource(FALLBACK_IDS.restSource, { type: "geojson", data: EMPTY });
        added++;
        this.restKey = "";
        this.restAlpha.clear();
      }
      if (!map.getSource(SOURCE_IDS.fallbackRoute)) {
        map.addSource(SOURCE_IDS.fallbackRoute, { type: "geojson", data: EMPTY, lineMetrics: true });
        added++;
        this.heroId = null;
      }
      const line = (id: string, source: string, paint: LineLayerSpecification["paint"]): void => {
        if (map.getLayer(id)) return;
        map.addLayer({ id, type: "line", source, layout: { "line-join": "round", "line-cap": "round" }, paint }, before);
        added++;
        for (const k of [...this.lastPaint.keys()]) if (k.startsWith(`${id}|`)) this.lastPaint.delete(k);
      };
      const fs: ExpressionSpecification = ["coalesce", ["feature-state", "a"], 0];
      line(FALLBACK_IDS.restShade, FALLBACK_IDS.restSource, { "line-color": "rgba(0,0,0,0)", "line-width": ["get", "w"], "line-opacity": fs, "line-blur": 1 });
      line(FALLBACK_IDS.restCore, FALLBACK_IDS.restSource, { "line-color": "rgba(0,0,0,0)", "line-width": ["get", "w"], "line-opacity": fs });
      line(LAYER_IDS.fallbackGlow, SOURCE_IDS.fallbackRoute, { "line-width": 14, "line-blur": 6, "line-opacity": 0, "line-gradient": progressGradient(0, 0, "", "") });
      line(FALLBACK_IDS.shade, SOURCE_IDS.fallbackRoute, { "line-width": 7.5, "line-blur": 1, "line-opacity": 0, "line-gradient": progressGradient(0, 0, "", "") });
      line(LAYER_IDS.fallbackCore, SOURCE_IDS.fallbackRoute, { "line-width": 3.5, "line-opacity": 0, "line-gradient": progressGradient(0, 0, "", "") });
      return added;
    } catch {
      return -1; // style not ready; the layer retries on the next style event
    }
  }

  setPaths(paths: PackedPath[]): void {
    this.paths = paths;
    this.restKey = "";
    this.heroId = null;
  }

  private paint(layer: string, prop: PaintName, value: PaintValue): void {
    const key = `${layer}|${prop}`;
    const s = JSON.stringify(value);
    if (this.lastPaint.get(key) === s) return;
    this.lastPaint.set(key, s);
    this.map.setPaintProperty(layer, prop, value);
  }

  update(f: FallbackFrame): void {
    if (this.destroyed) return;
    const map = this.map;
    if (!map.getLayer(LAYER_IDS.fallbackCore) && this.attach() < 0) return;
    const gain = f.light.glowGain;
    const shadeOn = f.light.shadePx > 0 && f.light.shade[3] > 0;
    const shade = cssRGBA(opaqueShade(f.light)); // opaque: overlapping routes never darken the casing

    /* rest: static lines with alpha */
    const restSrc = map.getSource(FALLBACK_IDS.restSource) as GeoJSONSource | undefined;
    const key = f.rest.map((r) => `${r.path.index}:${r.width}:${r.style}`).join(",");
    if (restSrc && key !== this.restKey) {
      this.restKey = key;
      restSrc.setData({
        type: "FeatureCollection",
        features: f.rest.map((r) => ({
          type: "Feature",
          id: r.path.index,
          properties: { w: r.width, s: r.style },
          geometry: { type: "LineString", coordinates: r.path.coords },
        })),
      });
      this.restAlpha.clear();
    }
    const seen = new Set<number>();
    for (const r of f.rest) {
      seen.add(r.path.index);
      const a = Math.round(r.alpha * 1000) / 1000;
      if (this.restAlpha.get(r.path.index) !== a) {
        this.restAlpha.set(r.path.index, a);
        map.setFeatureState({ source: FALLBACK_IDS.restSource, id: r.path.index }, { a });
      }
    }
    for (const id of [...this.restAlpha.keys()]) if (!seen.has(id)) this.restAlpha.delete(id);
    const p = f.palette;
    this.paint(FALLBACK_IDS.restCore, "line-color", ["match", ["get", "s"], "bloom", cssRGBA(p.bloom), "preview", cssRGBA(p.preview), cssRGBA(p.core)]);
    this.paint(FALLBACK_IDS.restShade, "line-color", shadeOn ? shade : "rgba(0,0,0,0)");
    this.paint(FALLBACK_IDS.restShade, "line-width", ["+", ["get", "w"], f.light.shadePx]);

    /* hero: stepped gradients */
    const src = map.getSource(SOURCE_IDS.fallbackRoute) as GeoJSONSource | undefined;
    const h = f.hero;
    if (src && (h?.path.id ?? null) !== this.heroId) {
      this.heroId = h?.path.id ?? null;
      src.setData(h ? { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: h.path.coords } } : EMPTY);
    }
    if (h) {
      const L = h.path.lengthM;
      const t0 = h.tailM / L;
      const t1 = h.headM >= L ? 1 : h.headM / L;
      const near = styleColor(p, h.style);
      const far = mixRGB(near, p.trail, h.trail);
      this.paint(LAYER_IDS.fallbackCore, "line-gradient", progressGradient(t0, t1, cssRGBA(far), cssRGBA(near)));
      this.paint(LAYER_IDS.fallbackCore, "line-width", h.width);
      this.paint(LAYER_IDS.fallbackCore, "line-opacity", h.alpha);
      this.paint(FALLBACK_IDS.shade, "line-gradient", progressGradient(t0, t1, shade, shade));
      this.paint(FALLBACK_IDS.shade, "line-width", h.width + f.light.shadePx);
      this.paint(FALLBACK_IDS.shade, "line-opacity", shadeOn ? h.alpha : 0);
      this.paint(LAYER_IDS.fallbackGlow, "line-gradient", progressGradient(t0, t1, cssRGBA(p.glow), cssRGBA(p.glow)));
      this.paint(LAYER_IDS.fallbackGlow, "line-width", h.width * 4);
      this.paint(LAYER_IDS.fallbackGlow, "line-opacity", Math.min(1, 0.35 * gain * h.glow) * h.alpha);
    } else {
      this.paint(LAYER_IDS.fallbackCore, "line-opacity", 0);
      this.paint(FALLBACK_IDS.shade, "line-opacity", 0);
      this.paint(LAYER_IDS.fallbackGlow, "line-opacity", 0);
    }

    /* comet: DOM marker */
    const c = f.comet;
    if (c && c.alpha > 0.002) {
      if (!this.marker) {
        const el = document.createElement("div");
        el.className = "ld-loc-gl-comet";
        el.setAttribute("aria-hidden", "true");
        const glow = document.createElement("div");
        glow.className = "ld-loc-gl-comet-glow";
        el.appendChild(glow);
        this.markerEl = el;
        this.marker = new maplibregl.Marker({ element: el, anchor: "center", pitchAlignment: "viewport", rotationAlignment: "viewport" });
      }
      const el = this.markerEl!;
      el.style.setProperty("--ld-loc-gl-comet-core", cssRGBA(p.comet));
      el.style.setProperty("--ld-loc-gl-comet-halo", cssRGBA(p.halo));
      el.style.setProperty("--ld-loc-gl-comet-alpha", String(Math.round(c.alpha * 1000) / 1000));
      el.style.setProperty("--ld-loc-gl-comet-scale", String(Math.round(c.scale * 1000) / 1000));
      this.marker.setLngLat(c.lngLat);
      if (!this.markerOn) {
        this.marker.addTo(map);
        this.markerOn = true;
      }
    } else if (this.marker && this.markerOn) {
      this.marker.remove();
      this.markerOn = false;
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.marker?.remove();
    this.marker = null;
    const map = this.map;
    try {
      for (const id of [LAYER_IDS.fallbackCore, FALLBACK_IDS.shade, LAYER_IDS.fallbackGlow, FALLBACK_IDS.restCore, FALLBACK_IDS.restShade]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      for (const id of [SOURCE_IDS.fallbackRoute, FALLBACK_IDS.restSource]) if (map.getSource(id)) map.removeSource(id);
    } catch {
      /* the map (or its style) is already gone */
    }
  }
}
